import { useState, useRef, useEffect, Fragment, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowsVertical,
  Filter,
  Column as ColumnIcon,
  Reset,
  Move,
  Pin,
  PinFilled,
  Search,
  Categories,
  ExpandAll,
  CollapseAll,
} from '@carbon/react/icons';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  flexRender,
  ColumnDef,
  Header,
  Table,
  Row,
  Column,
  SortingState,
  ColumnFiltersState,
  ColumnPinningState,
} from '@tanstack/react-table';
import { FloatingTooltip } from 'components/floating-tooltip';
import styles from './ownership-tanstack-table.module.scss';

// ============================================
// Helper Components
// ============================================

function DebouncedInput({
  value: initialValue,
  onChange,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        onChange(e.target.value);
      }}
    />
  );
}

function ColumnFilter({ column }: { column: Column<any, any> }) {
  const columnFilterValue = column.getFilterValue();
  const meta = column.columnDef.meta ?? {};
  const filterVariant = (meta as Record<string, unknown>).filterVariant as
    string | undefined;

  const sortedUniqueValues = useMemo(() => {
    if (filterVariant === 'select') {
      return Array.from(column.getFacetedUniqueValues().keys()).sort();
    }
    return [];
  }, [column, filterVariant]);

  if (filterVariant === 'range') {
    const [min, max] = column.getFacetedMinMaxValues() ?? [0, 0];
    return (
      <div
        className={`${styles['column-filter']} ${styles['column-filter--range']}`}
      >
        <input
          type="number"
          min={min}
          max={max}
          value={(columnFilterValue as [string, string])?.[0] ?? ''}
          onChange={(e) =>
            column.setFilterValue((old: [string, string]) => [
              e.target.value,
              old?.[1],
            ])
          }
          placeholder="Min"
        />
        <input
          type="number"
          min={min}
          max={max}
          value={(columnFilterValue as [string, string])?.[1] ?? ''}
          onChange={(e) =>
            column.setFilterValue((old: [string, string]) => [
              old?.[0],
              e.target.value,
            ])
          }
          placeholder="Max"
        />
      </div>
    );
  }

  if (filterVariant === 'select') {
    return (
      <select
        className={`${styles['column-filter']} ${styles['column-filter--select']}`}
        onChange={(e) => column.setFilterValue(e.target.value)}
        value={(columnFilterValue as string) ?? ''}
      >
        <option value="">All</option>
        {sortedUniqueValues.map((value: unknown) => (
          <option key={String(value)} value={String(value)}>
            {String(value)}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      className={`${styles['column-filter']} ${styles['column-filter--text']}`}
      type="text"
      value={(columnFilterValue as string) ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder="Search..."
    />
  );
}

interface DragState {
  isDragging: boolean;
  draggedId: string | null;
  draggedColumn: Column<any, any> | null;
  hoveredId: string | null;
}

function ColumnHeaderCell({
  header,
  table,
  dragState,
  setDragState,
}: {
  header: Header<any, any>;
  table: Table<any>;
  dragState: DragState;
  setDragState: React.Dispatch<React.SetStateAction<DragState>>;
}) {
  const { column } = header;
  const isPinned = column.getIsPinned();
  const columnDef = column.columnDef;
  const canDrag =
    !isPinned &&
    (columnDef as unknown as Record<string, unknown>).enableColumnOrdering !==
      false;

  const handleDragStart = (e: React.DragEvent) => {
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', column.id);
    e.dataTransfer.effectAllowed = 'move';

    const dragImage = document.createElement('div');
    dragImage.style.cssText = `
      position: absolute; top: -1000px; left: -1000px;
      display: flex; align-items: center; gap: 8px;
      background-color: #0f62fe; color: white;
      padding: 8px 16px; border-radius: 4px;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 14px; font-weight: 500;
      box-shadow: 0 4px 12px rgba(0,0,0,0.25);
      white-space: nowrap;
    `;
    const columnName = (columnDef.header as string) || column.id;
    dragImage.innerHTML = `<span>${columnName}</span>`;
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(
      dragImage,
      dragImage.offsetWidth / 2,
      dragImage.offsetHeight / 2
    );
    requestAnimationFrame(() => document.body.removeChild(dragImage));

    setDragState({
      isDragging: true,
      draggedId: column.id,
      draggedColumn: column,
      hoveredId: null,
    });
  };

  const handleDragEnd = () => {
    setDragState({
      isDragging: false,
      draggedId: null,
      draggedColumn: null,
      hoveredId: null,
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragState.draggedId === column.id || !dragState.isDragging) return;
    setDragState((prev) => ({ ...prev, hoveredId: column.id }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragState.draggedId === column.id || !dragState.isDragging) return;
    if (dragState.hoveredId !== column.id) {
      setDragState((prev) => ({ ...prev, hoveredId: column.id }));
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) {
      if (dragState.hoveredId === column.id) {
        setDragState((prev) => ({ ...prev, hoveredId: null }));
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const draggedColumnId = e.dataTransfer.getData('text/plain');
    const targetColumnId = column.id;
    if (draggedColumnId === targetColumnId) {
      setDragState({
        isDragging: false,
        draggedId: null,
        draggedColumn: null,
        hoveredId: null,
      });
      return;
    }
    const currentOrder = table.getState().columnOrder;
    const allColumns = table.getAllLeafColumns();
    const order = currentOrder.length
      ? [...currentOrder]
      : allColumns.map((c) => c.id);
    const draggedIndex = order.indexOf(draggedColumnId);
    const targetIndex = order.indexOf(targetColumnId);
    if (draggedIndex === -1 || targetIndex === -1) {
      setDragState({
        isDragging: false,
        draggedId: null,
        draggedColumn: null,
        hoveredId: null,
      });
      return;
    }
    const movingLeftToRight = draggedIndex < targetIndex;
    order.splice(draggedIndex, 1);
    const newTargetIndex = order.indexOf(targetColumnId);
    if (movingLeftToRight) {
      order.splice(newTargetIndex + 1, 0, draggedColumnId);
    } else {
      order.splice(newTargetIndex, 0, draggedColumnId);
    }
    table.setColumnOrder(order);
    setDragState({
      isDragging: false,
      draggedId: null,
      draggedColumn: null,
      hoveredId: null,
    });
  };

  const togglePin = () => {
    if (isPinned === 'left') {
      column.pin(false);
    } else {
      column.pin('left');
    }
  };

  const isBeingDragged = dragState.draggedId === column.id;
  const isHovered =
    dragState.hoveredId === column.id && dragState.draggedId !== column.id;

  return (
    <div
      className={`${styles['th-wrapper']} ${isPinned ? styles.pinned : ''} ${
        isBeingDragged ? styles['is-dragging'] : ''
      } ${isHovered ? styles['is-hovered'] : ''} ${
        dragState.isDragging ? styles['drag-active'] : ''
      }`}
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span className={styles['th-label']}>
        {flexRender(header.column.columnDef.header, header.getContext())}
      </span>
      <div className={styles['th-actions']}>
        {header.column.getCanSort() && (
          <button
            className={styles['th-action-btn']}
            onClick={header.column.getToggleSortingHandler()}
            title="Sort"
          >
            {header.column.getIsSorted() === 'asc' ? (
              <ArrowUp />
            ) : header.column.getIsSorted() === 'desc' ? (
              <ArrowDown />
            ) : (
              <ArrowsVertical />
            )}
          </button>
        )}
        {canDrag && (
          <span
            className={`${styles['th-action-btn']} ${styles['drag-handle']}`}
            title="Drag to reorder"
          >
            <Move />
          </span>
        )}
        <button
          className={`${styles['th-action-btn']} ${
            isPinned ? styles.active : ''
          }`}
          onClick={togglePin}
          title={isPinned ? 'Unpin column' : 'Pin column'}
        >
          {isPinned ? <PinFilled /> : <Pin />}
        </button>
      </div>
    </div>
  );
}

// ============================================
// Main TanStack Table Component
// ============================================

export interface GroupByOption {
  id: string;
  text: string;
}

interface TanStackTableProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  enableExpanding?: boolean;
  renderSubComponent?: (props: { row: Row<T> }) => React.ReactNode;
  searchPlaceholder?: string;
  /** ID of the currently selected row (matched against row.id) */
  selectedRowId?: string | null;
  /** Called when a row is clicked — receives the row's original data */
  onRowClick?: (row: T) => void;
  /** Extract a unique ID from a row for selection matching */
  getRowId?: (row: T) => string;
  /** Grouping options — if provided, a "Group" button appears in the toolbar */
  groupByOptions?: GroupByOption[];
  groupBy?: string;
  onGroupByChange?: (groupBy: string) => void;
  /** Extra React nodes rendered at the end of the toolbar (before reset) */
  toolbarExtra?: React.ReactNode;
  /** Optional function to compute a CSS class name for each row */
  getRowClassName?: (row: T) => string | undefined;
  /** Called when mouse enters/leaves a row — null on leave */
  onRowHover?: (row: T | null) => void;
}

export function TanStackTable<T>({
  data: initialData,
  columns: columnDefs,
  enableExpanding = false,
  renderSubComponent,
  searchPlaceholder = 'Search...',
  selectedRowId,
  onRowClick,
  getRowId,
  groupByOptions,
  groupBy,
  onGroupByChange,
  getRowClassName,
  onRowHover,
  toolbarExtra,
}: TanStackTableProps<T>) {
  const tableData = initialData;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: [],
    right: [],
  });
  const [expanded, setExpanded] = useState({});
  const [columnResizeMode] = useState<'onChange'>('onChange');
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedId: null,
    draggedColumn: null,
    hoveredId: null,
  });
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const groupMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showGroupMenu) return;
    const handler = (e: MouseEvent) => {
      if (
        groupMenuRef.current &&
        !groupMenuRef.current.contains(e.target as Node)
      ) {
        setShowGroupMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGroupMenu]);

  const columns = useMemo(() => {
    if (!enableExpanding) return columnDefs;
    const expanderColumn: ColumnDef<T, unknown> = {
      id: 'expander',
      header: 'Expander',
      cell: ({ row }: { row: Row<T> }) => {
        return row.getCanExpand() ? (
          <button
            className={styles['expander-btn']}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
          >
            {row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />}
          </button>
        ) : (
          <span />
        );
      },
      size: 36,
      enableSorting: false,
      enableColumnFilter: false,
      enableResizing: false,
    };
    return [expanderColumn, ...columnDefs];
  }, [columnDefs, enableExpanding]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      columnOrder,
      columnPinning,
      expanded,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onExpandedChange: setExpanded,
    getRowCanExpand: (row: Row<T>) => {
      const orig = row.original as Record<string, unknown>;
      return (
        enableExpanding &&
        Array.isArray(orig?.subRows) &&
        orig.subRows.length > 0
      );
    },
    getSubRows: (row: T) => (row as Record<string, unknown>).subRows as T[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    columnResizeMode,
    enableColumnPinning: true,
  });

  return (
    <div
      className={`${styles['tanstack-table-wrapper']} ${styles['layer-01']}`}
    >
      {/* Toolbar */}
      <div className={styles['table-toolbar']}>
        <div
          className={`${styles['toolbar-section']} ${styles['toolbar-section--search']}`}
        >
          <span className={styles['search-icon']}>
            <Search />
          </span>
          <DebouncedInput
            value={globalFilter ?? ''}
            onChange={(value) => setGlobalFilter(String(value))}
            className={styles['global-search']}
            placeholder={searchPlaceholder}
          />
        </div>

        {enableExpanding && (
          <>
            <FloatingTooltip content="Expand all" placement="bottom">
              <button
                className={styles['toolbar-btn']}
                onClick={() => table.toggleAllRowsExpanded(true)}
              >
                <ExpandAll />
              </button>
            </FloatingTooltip>
            <FloatingTooltip content="Collapse all" placement="bottom">
              <button
                className={styles['toolbar-btn']}
                onClick={() => table.toggleAllRowsExpanded(false)}
              >
                <CollapseAll />
              </button>
            </FloatingTooltip>
          </>
        )}

        {toolbarExtra}

        <FloatingTooltip content="Toggle column filters" placement="bottom">
          <button
            className={`${styles['toolbar-btn']} ${
              showColumnFilters ? styles['toolbar-btn--active'] : ''
            }`}
            onClick={() => setShowColumnFilters(!showColumnFilters)}
          >
            <Filter />
          </button>
        </FloatingTooltip>

        <div
          className={`${styles['toolbar-section']} ${styles['toolbar-section--visibility']}`}
        >
          <FloatingTooltip content="Toggle columns" placement="bottom">
            <button
              className={`${styles['toolbar-btn']} ${
                showColumnVisibility ? styles['toolbar-btn--active'] : ''
              }`}
              onClick={() => setShowColumnVisibility(!showColumnVisibility)}
            >
              <ColumnIcon />
            </button>
          </FloatingTooltip>
          {showColumnVisibility && (
            <div
              className={`${styles['dropdown-menu']} ${styles['dropdown-menu--open']}`}
            >
              <label className={styles['dropdown-item']}>
                <input
                  type="checkbox"
                  checked={table.getIsAllColumnsVisible()}
                  onChange={table.getToggleAllColumnsVisibilityHandler()}
                />
                Toggle All
              </label>
              <hr />
              {table.getAllLeafColumns().map((column) => (
                <label key={column.id} className={styles['dropdown-item']}>
                  <input
                    type="checkbox"
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                  />
                  {(column.columnDef.header as string) || column.id}
                </label>
              ))}
            </div>
          )}
        </div>

        {groupByOptions && onGroupByChange && (
          <div
            className={`${styles['toolbar-section']} ${styles['toolbar-section--group']}`}
            ref={groupMenuRef}
          >
            <FloatingTooltip content="Group rows" placement="bottom">
              <button
                className={`${styles['toolbar-btn']} ${
                  groupBy && groupBy !== 'none'
                    ? styles['toolbar-btn--active']
                    : ''
                }`}
                onClick={() => setShowGroupMenu((prev) => !prev)}
              >
                <Categories />
              </button>
            </FloatingTooltip>
            {showGroupMenu && (
              <div
                className={`${styles['dropdown-menu']} ${styles['dropdown-menu--open']}`}
              >
                {groupByOptions.map((opt) => (
                  <label key={opt.id} className={styles['dropdown-item']}>
                    <input
                      type="radio"
                      name="groupBy"
                      checked={groupBy === opt.id}
                      onChange={() => {
                        onGroupByChange(opt.id);
                        setShowGroupMenu(false);
                      }}
                    />
                    {opt.text}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <FloatingTooltip content="Reset all" placement="bottom">
          <button
            className={styles['toolbar-btn']}
            onClick={() => {
              setGlobalFilter('');
              setColumnFilters([]);
              setSorting([]);
              setShowColumnFilters(false);
              setShowColumnVisibility(false);
              setColumnPinning({ left: [], right: [] });
              setColumnOrder([]);
              if (onGroupByChange) onGroupByChange('none');
            }}
          >
            <Reset />
          </button>
        </FloatingTooltip>
      </div>
      {/* Table */}
      <div className={styles['table-scroll-container']}>
        <table className={styles['tanstack-table']}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isPinned = header.column.getIsPinned();
                  const isColumnDragging =
                    dragState.draggedId === header.column.id;
                  const isColumnHovered =
                    dragState.hoveredId === header.column.id &&
                    dragState.draggedId !== header.column.id;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${
                        isColumnDragging ? styles['column-dragging'] : ''
                      } ${isColumnHovered ? styles['column-hovered'] : ''}`}
                      style={{
                        width: header.getSize(),
                        maxWidth: header.getSize(),
                        position: isPinned ? 'sticky' : 'relative',
                        left: isPinned === 'left' ? 0 : undefined,
                        zIndex: isPinned ? 2 : 1,
                        backgroundColor: isPinned ? '#e0e0e0' : undefined,
                      }}
                    >
                      {header.column.id === 'expander' ? null : (
                        <ColumnHeaderCell
                          header={header}
                          table={table}
                          dragState={dragState}
                          setDragState={setDragState}
                        />
                      )}
                      {showColumnFilters && header.column.getCanFilter() && (
                        <ColumnFilter column={header.column} />
                      )}
                      {header.column.getCanResize() && (
                        <div
                          className={`${styles.resizer} ${
                            header.column.getIsResizing()
                              ? styles.isResizing
                              : ''
                          }`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr
                  onClick={(e) => {
                    if (row.getCanExpand()) {
                      row.getToggleExpandedHandler()();
                    } else if (onRowClick) {
                      e.stopPropagation();
                      onRowClick(row.original);
                    }
                  }}
                  className={
                    [
                      row.getCanExpand()
                        ? styles['clickable-row']
                        : selectedRowId &&
                            getRowId &&
                            getRowId(row.original) === selectedRowId
                          ? styles['selected-row']
                          : onRowClick
                            ? styles['clickable-row']
                            : undefined,
                      getRowClassName?.(row.original),
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  style={
                    row.getCanExpand() || onRowClick
                      ? { cursor: 'pointer' }
                      : undefined
                  }
                  onMouseEnter={
                    onRowHover ? () => onRowHover(row.original) : undefined
                  }
                  onMouseLeave={onRowHover ? () => onRowHover(null) : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isPinned = cell.column.getIsPinned();
                    const isColumnDragging =
                      dragState.draggedId === cell.column.id;
                    const isColumnHovered =
                      dragState.hoveredId === cell.column.id &&
                      dragState.draggedId !== cell.column.id;

                    const handleCellDragOver = (e: React.DragEvent) => {
                      if (
                        !dragState.isDragging ||
                        dragState.draggedId === cell.column.id
                      )
                        return;
                      e.preventDefault();
                      if (dragState.hoveredId !== cell.column.id) {
                        setDragState((prev) => ({
                          ...prev,
                          hoveredId: cell.column.id,
                        }));
                      }
                    };

                    const handleCellDrop = (e: React.DragEvent) => {
                      e.preventDefault();
                      const draggedColumnId =
                        e.dataTransfer.getData('text/plain');
                      const targetColumnId = cell.column.id;
                      if (draggedColumnId === targetColumnId) {
                        setDragState({
                          isDragging: false,
                          draggedId: null,
                          draggedColumn: null,
                          hoveredId: null,
                        });
                        return;
                      }
                      const currentOrder = table.getState().columnOrder;
                      const allColumns = table.getAllLeafColumns();
                      const order = currentOrder.length
                        ? [...currentOrder]
                        : allColumns.map((c) => c.id);
                      const draggedIndex = order.indexOf(draggedColumnId);
                      const targetIndex = order.indexOf(targetColumnId);
                      if (draggedIndex === -1 || targetIndex === -1) {
                        setDragState({
                          isDragging: false,
                          draggedId: null,
                          draggedColumn: null,
                          hoveredId: null,
                        });
                        return;
                      }
                      const movingLeftToRight = draggedIndex < targetIndex;
                      order.splice(draggedIndex, 1);
                      const newTargetIndex = order.indexOf(targetColumnId);
                      if (movingLeftToRight) {
                        order.splice(newTargetIndex + 1, 0, draggedColumnId);
                      } else {
                        order.splice(newTargetIndex, 0, draggedColumnId);
                      }
                      table.setColumnOrder(order);
                      setDragState({
                        isDragging: false,
                        draggedId: null,
                        draggedColumn: null,
                        hoveredId: null,
                      });
                    };

                    return (
                      <td
                        key={cell.id}
                        className={`${
                          isColumnDragging ? styles['column-dragging'] : ''
                        } ${isColumnHovered ? styles['column-hovered'] : ''}`}
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          position: isPinned ? 'sticky' : 'relative',
                          left: isPinned === 'left' ? 0 : undefined,
                          zIndex: isPinned ? 1 : 0,
                          backgroundColor: isPinned ? '#f4f4f4' : undefined,
                        }}
                        onDragOver={handleCellDragOver}
                        onDrop={handleCellDrop}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
                {row.getIsExpanded() && renderSubComponent && (
                  <tr className={styles['expanded-row']}>
                    <td colSpan={row.getVisibleCells().length}>
                      {renderSubComponent({ row })}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Footer */}
      <div className={styles['table-footer']}>
        <span className={styles['row-count']}>
          {table.getFilteredRowModel().rows.length} items
        </span>
      </div>
    </div>
  );
}
